import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Package, MapPin, Clock, CheckCircle, Truck, XCircle, Search, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { label: 'Order Placed', icon: Package, key: 'pending_payment' },
  { label: 'Processing', icon: Clock, key: 'processing' },
  { label: 'Shipped', icon: Truck, key: 'shipped' },
  { label: 'Delivered', icon: CheckCircle, key: 'delivered' },
];

const getStatusStep = (status) => {
  const idx = STEPS.findIndex(s => s.key === status);
  return idx === -1 ? 0 : idx;
};

const TrackOrderPage = () => {
  const [params, setParams] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get('order_id') || '');
  const [email, setEmail] = useState(params.get('email') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOrder = useCallback(async (oid, em) => {
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/track`, {
        params: { order_id: oid.trim(), email: em.trim() }
      });
      setOrder(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Order not found. Double-check your Order ID and email.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch if both params present in URL
  useEffect(() => {
    const oid = params.get('order_id');
    const em = params.get('email');
    if (oid && em) fetchOrder(oid, em);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) return;
    setParams({ order_id: orderId.trim(), email: email.trim() });
    fetchOrder(orderId, email);
  };

  const currentStep = order ? getStatusStep(order.status) : 0;

  return (
    <div className="min-h-screen bg-[#050505] py-12" data-testid="track-order-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Track Your Order
          </h1>
          <p className="text-[#A1A7B5] text-sm">
            Enter your Order ID and the email used at checkout. No login required.
          </p>
        </div>

        {/* Lookup form */}
        <form onSubmit={handleSubmit} className="glass-card p-6 mb-8" data-testid="track-order-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Order ID</label>
              <Input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="e.g. 6a12897dc1be6669efd8b0a1"
                className="bg-[#111621] border-[#1E2532] text-white"
                data-testid="track-order-id-input"
              />
            </div>
            <div>
              <label className="block text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-[#111621] border-[#1E2532] text-white"
                data-testid="track-order-email-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={loading || !orderId.trim() || !email.trim()}
            data-testid="track-order-submit"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Looking up…' : 'Track Order'}
          </Button>
        </form>

        {error && (
          <div className="glass-card p-4 mb-8 border border-red-500/40" data-testid="track-order-error">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {order && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-8">
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Order #{order.id.slice(-8)}
                </h2>
                <p className="text-[#A1A7B5] text-sm">
                  Placed {new Date(order.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className="text-2xl font-bold text-[#00F0FF]" data-testid="track-order-total">
                ${order.total.toFixed(2)}
              </span>
            </div>

            {/* Status tracker */}
            {order.status !== 'cancelled' ? (
              <div className="glass-card p-6 mb-8" data-testid="track-order-status">
                <h3 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Order Status
                </h3>
                <div className="relative">
                  <div className="flex justify-between">
                    {STEPS.map((step, index) => {
                      const Icon = step.icon;
                      const isCompleted = index <= currentStep;
                      const isCurrent = index === currentStep;
                      return (
                        <div key={index} className="flex flex-col items-center relative z-10">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                            isCompleted ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'
                          } ${isCurrent ? 'animate-pulse-glow' : ''}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <span className={`text-xs sm:text-sm mt-2 text-center ${isCompleted ? 'text-white' : 'text-[#A1A7B5]'}`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="absolute top-6 left-0 right-0 h-0.5 bg-[#1E2532]">
                    <div
                      className="h-full bg-[#00F0FF] transition-all duration-500"
                      style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
                    />
                  </div>
                </div>

                {order.tracking_number && (
                  <div className="mt-8 p-4 bg-[#111621] rounded-lg border border-[#00F0FF]/30" data-testid="track-order-tracking-block">
                    <p className="text-xs text-[#A1A7B5] uppercase tracking-wide mb-1">Tracking Number</p>
                    <p className="text-[#00F0FF] font-mono text-sm mb-3">{order.tracking_number}</p>
                    {order.tracking_url && (
                      <a
                        href={order.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-white bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 border border-[#00F0FF]/40 px-4 py-2 rounded-md transition-colors"
                        data-testid="track-order-carrier-link"
                      >
                        Track with Carrier →
                      </a>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-card p-6 mb-8 border-red-400/50">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-8 h-8 text-red-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-400">Order Cancelled</h3>
                    <p className="text-[#A1A7B5] text-sm">If you didn't request this, please contact support.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Order Items
                </h3>
                <div className="space-y-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-[#111621] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{item.name}</p>
                        <p className="text-[#A1A7B5] text-xs">Qty {item.quantity}</p>
                      </div>
                      <p className="text-[#00F0FF] text-sm font-semibold ml-3">${item.subtotal.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {order.shipping_address && (
                  <div className="glass-card p-5">
                    <div className="flex items-center space-x-2 mb-3">
                      <MapPin className="w-4 h-4 text-[#00F0FF]" />
                      <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Ships To</h3>
                    </div>
                    <div className="text-[#A1A7B5] text-sm space-y-0.5">
                      <p className="text-white">{order.shipping_address.firstName} {order.shipping_address.lastName}</p>
                      <p>{order.shipping_address.address}</p>
                      <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}</p>
                      <p>{order.shipping_address.country}</p>
                    </div>
                  </div>
                )}
                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-[#A1A7B5]">
                      <span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#A1A7B5]">
                      <span>Shipping ({order.shipping_method})</span>
                      <span>{order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-white font-bold text-base pt-2 border-t border-[#1E2532]">
                      <span>Total</span><span className="text-[#00F0FF]">${order.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
